import { Box, Button, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useState } from 'react'
import { getAssetUrl } from '../config'
import type { SiteInfo } from '../hooks'
import LoginDialog from '../LoginRedirector/LoginDialog'
import type { SelectedRole } from '../LoginRedirector/RoleSelectionDialog'

// Kept at the original 768px cutoff rather than MUI's sm/md breakpoints
const MOBILE = '@media (max-width: 768px)'

const cardSx = {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
} as const

const sx = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem',
    background: 'rgb(255 255 255 / 0.95)',
    boxShadow: '0 2px 8px rgb(0 0 0 / 0.1)',
    [MOBILE]: { padding: '1rem' },
  },
  banner: { display: 'flex', alignItems: 'center', gap: '1rem' },
  logo: { height: 48, width: 'auto', [MOBILE]: { height: 36 } },
  siteName: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#333',
    margin: 0,
    [MOBILE]: { fontSize: '1.25rem' },
  },
  loginButton: { padding: '0.5rem 2rem', fontSize: '1rem', fontWeight: 600 },
  main: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '3rem 2rem',
    [MOBILE]: { padding: '2rem 1rem' },
  },
  intro: {
    ...cardSx,
    padding: '2.5rem',
    marginBottom: '2rem',
    [MOBILE]: { padding: '1.5rem' },
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#333',
    margin: '0 0 1.5rem',
    textAlign: 'center',
    [MOBILE]: { fontSize: '1.5rem' },
  },
  description: {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: '#555',
    margin: 0,
    textAlign: 'center',
    [MOBILE]: { fontSize: '1rem' },
  },
  section: {
    ...cardSx,
    padding: '2rem',
    marginBottom: '1.5rem',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgb(0 0 0 / 0.15)',
    },
    [MOBILE]: { padding: '1.5rem' },
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#667eea',
    margin: '0 0 1rem',
    [MOBILE]: { fontSize: '1.25rem' },
  },
  sectionText: { fontSize: '1rem', lineHeight: 1.8, color: '#666', margin: 0 },
} satisfies Record<string, SxProps<Theme>>

interface HomePageProps {
  onLoginSuccess: (sites: SiteInfo[], role: SelectedRole, isAppAdmin: boolean) => void
}

export default function HomePage({ onLoginSuccess }: HomePageProps) {
  const [showLoginDialog, setShowLoginDialog] = useState(false)

  const handleLoginClick = () => {
    setShowLoginDialog(true)
  }

  const handleLoginSuccess = (
    sites: SiteInfo[],
    role: SelectedRole,
    isAppAdmin: boolean,
  ) => {
    setShowLoginDialog(false)
    onLoginSuccess(sites, role, isAppAdmin)
  }

  const handleCloseDialog = () => {
    setShowLoginDialog(false)
  }

  return (
    <>
      <Box sx={sx.container}>
        <Box component="header" sx={sx.header}>
          <Box sx={sx.banner}>
            <Box
              component="img"
              src={getAssetUrl('/logo.svg')}
              alt="Logo"
              sx={sx.logo}
            />
            <Typography component="h1" sx={sx.siteName} data-testid="site-name">
              几点几体育
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleLoginClick}
            sx={sx.loginButton}
            data-testid="login-button"
          >
            登录
          </Button>
        </Box>

        <Box component="main" sx={sx.main}>
          <Box component="section" sx={sx.intro}>
            <Typography component="h2" sx={sx.title}>
              "网球几点几" APP 介绍
            </Typography>
            <Typography component="p" sx={sx.description}>
              "网球几点几"APP是专为网球爱好者打造的水平评估 + 以球会友 +
              赛事管理的一体化工具
            </Typography>
          </Box>

          <Box component="section" sx={sx.section}>
            <Typography component="h3" sx={sx.sectionTitle}>
              精准水平评级：科学定义你的网球实力
            </Typography>
            <Typography component="p" sx={sx.sectionText}>
              通过记录用户真实对战数据（如回合数、得分效率等），结合行业通用的 1.0-7.0
              评级标准，科学评估网球水平，让实力可视化。助力用户明确提升方向，也为约球、参赛提供实力参考。
            </Typography>
          </Box>

          <Box component="section" sx={sx.section}>
            <Typography component="h3" sx={sx.sectionTitle}>
              同频约球互动：轻松找到匹配球友
            </Typography>
            <Typography component="p" sx={sx.sectionText}>
              支持按水平等级（如 2.5
              级、3.5级）筛选附近友谊赛，或自主发起约球，轻松实现'随时约、同水平打"。既能通过同水平对抗提升球技，又能结识兴趣相投的球友，兼顾运动与社交。
            </Typography>
          </Box>

          <Box component="section" sx={sx.section}>
            <Typography component="h3" sx={sx.sectionTitle}>
              俱乐部赛事管理：提升运营效率
            </Typography>
            <Typography component="p" sx={sx.sectionText}>
              俱乐部可一键发起赛事、自动生成赛程表、同步通知参赛用户，减少人工统计成本，提升赛事组织效率。让俱乐部赛事组织更高效，也让会员参赛流程更便捷，提升俱乐部活跃度。
            </Typography>
          </Box>
        </Box>
      </Box>

      {showLoginDialog && (
        <LoginDialog onLoginSuccess={handleLoginSuccess} onClose={handleCloseDialog} />
      )}
    </>
  )
}
